import { Travelers } from '@booking/entities/booking-arrangement';
import { PaxType } from '@booking/entities/pax-type';
import { FlightResultsMobile } from '@app/entities/flight-results';
import { ReservationStatus } from '@user-history/entities/reservation-status';
import { CrossSellingReservationItem } from '@shared/cross-selling/entities/cross-selling';
import { RequestSettingsBase } from '@app/entities/request-settings';
import { AirSearchPayloadTrips } from '@flight-results/entities/airSearchPayload';

export type UserHistoryItem = Readonly<{
  Id: number,
  CurrencyCode: string,
  TotalPrice: number,
  ReservationStatus: string,
  PaymentStatus: string,
  PaymentReferenceCode?: string,
  ReservationItems: ReservationItems[],
  order_summary_web_url: string,
  CrossSellingByProducts: CrossSellingReservationItem[],
  AssistanceItem: AssistanceByReservationItem
}>;

export type AssistanceByReservationItem = Readonly<{
  CreatedDate: string;
  Description: string;
  Destination: string;
  EndDate: string;
  ImageUrl: string;
  Origin: string;
  ConfirmationNumber: string;
  ReservationItemType: ReservationType;
  ReservationStatus: ReservationStatus;
  StartDate: string;
  Company: {
    Code: string,
    Name: string
  };
  VisibleInTheListResume: boolean;
}>;

export type ReservationItems = Readonly<{
  StartDate: string;
  EndDate: string;
  Origin: string;
  Destination: string;
  ConfirmationNumber: string;
  ReservationItemType: ReservationType;
  Description: string;
  AirlineCode: string;
  AirTripType: AirTripTypeEnum;
  ReservationStatus: ReservationStatus | null;
  Model?: string;
  ImageUrl?: string;
  CreatedDate?: string;
  Company?: {
    Code: string;
    Name: string
  };
  ExtraReservationType?: ReservationType;
  ExtraReservationDescription?: string;
  AllExtrasDescription?: string[];
  MicroStay?: boolean;
}>;

export type UserHistoryItemVmOrder = Readonly<{
  ReservationStartDateFormat: string;
  ReservationEndDateFormat: string;
}> & UserHistoryItem;

export type BookingPoliciesPayload = Readonly<{
  AirReservationId: number;
  TravelItineraryId: number;
  RequestSettings: RequestSettingsBase;
  SessionToken?: string;
  CurrencyCode: string;
}>;

export type SectorChangeTrip = Readonly<{
  SectorNumber: number,
  DepartureDate: string,
  DeparturePoint: string,
  ArrivalPoint: string,
  ReservationIndex?: number,
  SectorChange?: boolean
}>;

export type ExchangeSearchIdentificator = Readonly<{
  TravelItineraryId: number | string,
  AirReservationId: number | string
}>;

export type SearchTraveler = Readonly<{
  PaxType: PaxType
}>;

export type ExchangeSearchParamsSectors = Readonly<{
  Trips: AirSearchPayloadTrips[],
  Passengers: Travelers[] | SearchTraveler[],
  exchangeDisabledSector: AirSearchPayloadTrips[],
  RouteChangeIsDisabled?: boolean;
  baggageIncluded?: boolean,
  originalDeparture?: string,
  originalArrival?: string,
  originalRoute?: string,
  originalDepartureDateTime?: string,
  originalArrivalDateTime?: string,
  departureSelectionDisabled?: boolean,
  withTravelAssistance?: boolean,
  exchangeWithAssistanceAndLongerDuration?: boolean,
  exchangeWithAssistanceAndShorterOrEqualDuration?: boolean,
}> & ExchangeSearchIdentificator;

export type ExchangeSearchParamsBody = Readonly<{
  Trips: SectorChangeTrip[]
}> & ExchangeSearchIdentificator;

export type ExchangeSearchPayload = Readonly<{
  Adults: string,
  Children: string,
  Infants: string,
  RequestSettings: RequestSettingsBase,
  CurrencyCode: string,
  SessionToken?: string,
  DiscardPendingReservations: boolean,
}> & ExchangeSearchParamsBody;

export type BookingPoliciesResponse = Readonly<{
  IsExchangeable: boolean;
  IsRefundable: boolean;
  IsVoidable: boolean;
  ProviderExchangingRemarks: string;
  Success: boolean;
  IsRouteChangeable: boolean | undefined;
  Sectors: BookingPoliciesSector[] | undefined;
}>;

export type BookingPoliciesSector = Readonly<{
  SectorNumber: number;
  IsExchangeable: boolean;
}>;

export type ExchangeSearchResponse = Readonly<{
  Results: FlightResultsMobile[],
  SearchId: string,
  ResultsCount: number,
  ResultsAvailableUntil: string
}>;

export type ExchangeBookPayload = Readonly<{
  OptionId: number,
  SearchId: string,
  ContactEmail: string,
  ContactPhone: string,
  ContextApp: string,
  SessionToken: string
}> & ExchangeSearchIdentificator;

export type ExchangeBookResponse = Readonly<{
  UrlRedirect: string
}>;

export enum ReservationType {
  Hotel = 'HOTEL',
  Flight = 'AIR',
  Bus = 'BUS',
  Extra = 'EXTRA',
  AirPackage = 'AIR_PACKAGE',
  Auto = 'VEHICLE',
  Assistance = 'ASSISTANCE',
  Activity = 'ACTIVITY',
}

export const ReservationTypeByEvents = {
  [ReservationType.Flight]: 'FLIGHTS',
  [ReservationType.AirPackage]: 'PACKAGES'
};

export enum SectorTripNumber {
  DEPARTURE = 1,
  RETURN = 2
}

export enum AirTripTypeEnum {
  RT = 'RT',
  OW = 'OW'
}
